/** @format */
import { useTranslation } from 'next-i18next';
import NextLink from 'next/link';
import React, {
    Fragment,
    FunctionComponent,
    ReactNode,
    useContext,
} from 'react';
import styled from 'styled-components';

import { NavigationLinks } from '@toucan/common/services/navigation/types';
import { ProductCategory } from '@toucan/common/services/taxonomy/config/types';

import { MarketCode, MarketContext } from '@toucan/client/global/market';
import { WrappedLink } from '@toucan/client/global/nextjs';

import { GridRepeater } from '@toucan/client/shared/components/grid-repeater';
import {
    Link,
    LinkColor,
    LinkSize,
    LinkVariant,
} from '@toucan/client/shared/components/link';
import {
    TabContent,
    TabPanel,
    TabProps,
} from '@toucan/client/shared/components/tab-panel';

import {
    baseTabStyles,
    categoryTabStyles,
    styles,
} from './desktop-main-nav.styles';

type PopularBrand = {
    name: string;
    id: number;
    logo: ReactNode;
};

// eslint-disable-next-line @mpb-com/mpb/require-constants-file -- Legacy code: do not use as an example.
export enum DesktopMainNavTabVariant {
    CATEGORIES,
    BRANDS,
}

// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type TaxonomyTabProps = {
    categories: ProductCategory[];
    initialDepth?: number;
};

// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type BrandTabProps = {
    brands: PopularBrand[];
};

// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type DesktopMainNavProps = {
    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;

    navigationLinks: NavigationLinks;
};

type WithActiveTabAndBrands = {
    brands: PopularBrand[];
};

// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type DesktopMainNavPropsWithActiveTab = DesktopMainNavProps &
    WithActiveTabAndBrands;

// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type BaseTabProps = {
    isActive: boolean;
};

const StyledDesktopMainNav = styled.nav`
    ${styles}
`;

// eslint-disable-next-line @mpb-com/mpb/single-styled-component -- Legacy code: do not use as an example.
const StyledTab = styled.div<BaseTabProps>`
    ${baseTabStyles}
`;

// eslint-disable-next-line @mpb-com/mpb/single-styled-component -- Legacy code: do not use as an example.
const StyledTaxonomyTab = styled.div<BaseTabProps>`
    ${baseTabStyles}
    ${categoryTabStyles}
`;

/**
 * Renders the CategoriesTab
 *
 * @param {CategoriesTabProps} - The properties required by the CategoriesTab component
 * @returns {ReactElement} - The brand tab
 */
const TaxonomyTab: FunctionComponent<TaxonomyTabProps> = ({
    categories,
    initialDepth,
}: TaxonomyTabProps) => {
    function renderCategories(
        categories: ProductCategory[],
        // eslint-disable-next-line @typescript-eslint/no-inferrable-types -- Legacy code: do not use as an example.
        depth: number = 0
    ): ReactNode {
        return categories.map(
            (category: ProductCategory, index: number): ReactNode => {
                const linkSize =
                    depth === 0 // eslint-disable-line no-nested-ternary -- Legacy code: do not use as an example.
                        ? LinkSize.LARGE
                        : depth === 1
                          ? LinkSize.MEDIUM
                          : LinkSize.SMALL;

                return (
                    <ul
                        className="category-tree"
                        // eslint-disable-next-line react/no-array-index-key -- Legacy code: do not use as an example.
                        key={`category-tree--${index}`}
                        data-depth={depth}
                    >
                        <li className="category">
                            <NextLink
                                prefetch={false}
                                href={category.templatePath}
                                as={category.url}
                                passHref={true}
                                legacyBehavior
                            >
                                <Link
                                    href={category.url}
                                    size={linkSize}
                                    linkColor={LinkColor.NAVIGATION}
                                    role="menuitem"
                                >
                                    {category.title}
                                </Link>
                            </NextLink>

                            {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                            {category.categories &&
                                renderCategories(
                                    category.categories,
                                    depth + 1
                                )}
                        </li>
                    </ul>
                );
            }
        );
    }

    return (
        <ul className="columns">
            <li>{renderCategories(categories, initialDepth)}</li>
        </ul>
    );
};

const BrandTaxonomyTab = ({ categories, initialDepth }: TaxonomyTabProps) => {
    const { t } = useTranslation();
    const { activeMarket } = useContext(MarketContext);

    const searchSlug = t('search', { ns: 'urls' });

    const browseAllLinkProps =
        activeMarket.code === MarketCode.FR_FR
            ? {
                  href: '/search?q=*',
                  as: `/${activeMarket.slug}/${searchSlug}?q=*`,
              }
            : {
                  href: '/brands',
                  as: `/${activeMarket.slug}/${t('brands', { ns: 'urls' })}`,
              };

    return (
        <Fragment>
            <TaxonomyTab categories={categories} initialDepth={initialDepth} />
            <WrappedLink
                {...browseAllLinkProps}
                className="browse-all-link"
                data-testid="desktop-menu__browse-all-link"
            >
                {t('Browse all brands')}
            </WrappedLink>
        </Fragment>
    );
};

// eslint-disable-next-line @mpb-com/mpb/single-styled-component -- Legacy code: do not use as an example.
/* eslint-disable @mpb-com/mpb/use-design-tokens -- Legacy code: do not use as an example. */
// eslint-disable-next-line @mpb-com/mpb/single-styled-component -- Legacy code: do not use as an example.
const StyledOriginalContentLink = styled(Link)`
    small {
        color: var(--colors-text-primary);
        font-size: 1.2rem;
        line-height: 1.8rem;
        font-weight: 300;
    }
`;
/* eslint-enable @mpb-com/mpb/use-design-tokens -- Legacy code: do not use as an example. */

const OriginalContentTab = ({
    originalContentLinks,
}: {
    originalContentLinks: NavigationLinks['originalContentLinks'];
}) => {
    const { t } = useTranslation();
    const { activeMarket } = useContext(MarketContext);

    const contentSlug = t('content', { ns: 'urls' });
    const links = originalContentLinks.map(({ urlSlug, name, teaser }) => {
        return (
            <NextLink
                prefetch={false}
                href={`/content/category?slug=${urlSlug}`}
                as={`/${activeMarket.slug}/${contentSlug}/${urlSlug}/`}
                passHref={true}
                key={urlSlug}
            >
                <StyledOriginalContentLink variant={LinkVariant.CUSTOM}>
                    <Link linkColor={LinkColor.NAVIGATION}>{name}</Link>
                    <br />
                    <small>{teaser}</small>
                </StyledOriginalContentLink>
            </NextLink>
        );
    });

    return (
        <Fragment>
            <GridRepeater gridColumns={2}>{links}</GridRepeater>
            <WrappedLink
                className="see-all-original-content-link"
                href="/content"
                as={`/${activeMarket.slug}/${contentSlug}`}
            >
                {t('See all original content')}
            </WrappedLink>
        </Fragment>
    );
};

export const DesktopMainNav: FunctionComponent<DesktopMainNavProps> = ({
    navigationLinks,
    ...otherProps
}: DesktopMainNavProps) => {
    const { t } = useTranslation();

    const tabs: TabProps[] = [
        {
            children: t('Shop By Category'),
            'data-testid': 'desktop-menu__categories-tab-button',
        },
        {
            children: t('Shop By Brand'),
            'data-testid': 'desktop-menu__brands-tab-button',
        },
        {
            children: t('Original content'),
            'data-testid': 'desktop-menu__content-tab-button',
        },
    ];
    const categories = navigationLinks.categoryLinks;
    const brands = navigationLinks.featuredBrandLinks;

    function renderTabContent({ activeTabIndex }: TabContent): ReactNode {
        return (
            <Fragment>
                <StyledTaxonomyTab
                    isActive={activeTabIndex === 0}
                    data-testid="desktop-menu__categories-tab"
                >
                    {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                    {categories && <TaxonomyTab categories={categories} />}
                </StyledTaxonomyTab>
                <StyledTaxonomyTab
                    isActive={activeTabIndex === 1}
                    data-testid="desktop-menu__brands-tab"
                >
                    {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                    {brands && (
                        <BrandTaxonomyTab
                            categories={brands}
                            initialDepth={1}
                        />
                    )}
                </StyledTaxonomyTab>
                <StyledTab
                    isActive={activeTabIndex === 2}
                    data-testid="desktop-menu__content-tab"
                >
                    <OriginalContentTab
                        originalContentLinks={
                            navigationLinks.originalContentLinks
                        }
                    />
                </StyledTab>
            </Fragment>
        );
    }

    return (
        <StyledDesktopMainNav {...otherProps}>
            <TabPanel
                tabs={tabs}
                className="menu-tabs"
                data-testid="desktop-menu__tabs"
            >
                {renderTabContent}
            </TabPanel>
        </StyledDesktopMainNav>
    );
};
